import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Me, api, onUnauthorized } from "./api";

const TOKEN_KEY = "kepler-mcp-portal:token";

type AuthContextValue = {
  token: string | null;
  me: Me | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    sessionStorage.getItem(TOKEN_KEY),
  );
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState<boolean>(token !== null);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMe(null);
  }, []);

  useEffect(() => {
    onUnauthorized(() => logout());
  }, [logout]);

  useEffect(() => {
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .me(token)
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch(() => {
        if (!cancelled) logout();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api.login(username, password);
    sessionStorage.setItem(TOKEN_KEY, r.access_token);
    setToken(r.access_token);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    const r = await api.refresh(token);
    sessionStorage.setItem(TOKEN_KEY, r.access_token);
    setToken(r.access_token);
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({ token, me, loading, login, logout, refresh }),
    [token, me, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() outside AuthProvider");
  return ctx;
}
